import React from 'react';
import {
  Table,
  useTable,
  useTheme,
  IconButton,
  DeleteIcon,
  MRT_Row,
  AddCircleIcon,
  Button,
} from '@cherre-frontend/ui';
import { useSchemaColumnDefinitions } from 'src/products/models-library/hooks/useColumnDefinitions';
import { Control } from 'react-hook-form';
import { TargetTableFormValues, TableMode } from '../schema';

export type SchemaTableProps = {
  control: Control<TargetTableFormValues>;
  tableMode: TableMode;
  handleFieldNameBlur?: () => void;
  targetTableState?: TargetTableFormValues['tableData'] | undefined;
  setTargetTableState?: (tableData: TargetTableFormValues['tableData']) => void;
  dsp?: boolean;
};

export const SchemaTable: React.FC<SchemaTableProps> = ({
  control,
  handleFieldNameBlur,
  targetTableState,
  setTargetTableState,
  dsp = false,
  tableMode,
}) => {
  const theme = useTheme();

  const handleAddRowClick = () => {
    setTargetTableState?.([
      ...(targetTableState ?? []),
      {
        name: '',
        displayName: '',
        type: '',
        description: '',
        ...(dsp ? { required: true } : {}),
        dsp: { required: false, defaultValue: '' },
      },
    ]);
  };
  const handleDeleteRowClick =
    (row: MRT_Row<TargetTableFormValues['tableData'][number]>) =>
    (_event: React.MouseEvent) => {
      setTargetTableState?.(
        (targetTableState ?? []).filter((_, index) => index !== row.index)
      );
      handleFieldNameBlur?.();
    };
  const columns = useSchemaColumnDefinitions({
    control,
    onFieldNameBlur: handleFieldNameBlur,
    tableMode,
    dsp,
  });
  const table = useTable({
    data: targetTableState ?? [],
    layoutMode: 'grid',
    skeletonRowCount: 5,
    columns,
    enableSorting: false,
    enableColumnResizing: true,
    enableRowSelection: false,
    enableRowActions: tableMode === TableMode.Create,
    positionActionsColumn: 'last',
    displayColumnDefOptions: {
      'mrt-row-actions': {
        size: 30,
      },
    },
    renderRowActions: ({ row }) => (
      <IconButton onClick={handleDeleteRowClick(row)} color='error'>
        <DeleteIcon />
      </IconButton>
    ),
    enableBottomToolbar: true,
    enablePagination: tableMode !== TableMode.Create,
    enableStickyHeader: true,
    muiTableProps: {
      sx: {
        height: '100%',
      },
    },
    muiTablePaperProps: {
      sx: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        flex: 1,
        borderRadius: '16px 16px 16px 16px',
        maxHeight: '800px',
      },
      elevation: 0,
    },
    muiTableContainerProps: {
      sx: {
        flex: 1,
        border: `1px ${theme.palette.grey[300]} solid`,
        borderRadius: '16px',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: theme.palette.secondary.main,
        color: 'white',
      },
    },
    muiTableBodyCellProps: {
      sx: {
        backgroundColor: 'white',
        border: '0px',
      },
    },
    muiTableBodyRowProps: {
      sx: {
        '&:hover td': {
          backgroundColor: theme.palette.grey[200],
        },
      },
    },
    manualPagination: false,
    manualSorting: false,
    initialState: {
      pagination: { pageSize: 10, pageIndex: 0 },
      columnPinning: { right: ['mrt-row-actions'] },
    },
    getRowId: (row, index) => `schema-tab-${row.name}${index.toString()}`,
    //TODO: @gaspardetienne97 remove memoMode to the table if it's not needed
    // memoMode: 'cells', // 'cells' | 'rows' | 'table-body'
    renderBottomToolbarCustomActions: () =>
      tableMode === TableMode.Create && (
        <Button startIcon={<AddCircleIcon />} onClick={handleAddRowClick}>
          Add new target field
        </Button>
      ),
  });

  return <Table table={table} />;
};

SchemaTable.displayName = 'SchemaTable';
export default SchemaTable;
